import React, { useState, useRef, useEffect } from 'react'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload, faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import useAuth from '../hooks/useAuth'
import { toast } from 'sonner'
const SupportingDocs = ({ uploadUrl, deleteUrl, id, isBooked }) => {
  const [fileList, setFileList] = useState([])
  const [existingFiles, setExistingFiles] = useState([])
  const [errorMsg, setErrorMsg] = useState('')
  const wrapperRef = useRef(null)
  const fileInputRef = useRef(null) // Added ref to reset input
  const axios = useAxiosPrivate()
  const { auth } = useAuth()
  const maxSize = 10 * 1024 * 1024
  const acceptedTypes = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ]

  useEffect(() => {
    const stored = localStorage.getItem('uploadedFiles')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed.attachments)) {
          setExistingFiles(parsed.attachments)
        }
      } catch (err) {
        console.error('Failed to parse uploadedFiles from localStorage:', err)
      }
    } else {
      fetchBookingAttachments()
    }
  }, [])

  const fetchBookingAttachments = async () => {
    const res = await axios.get(`bookings/user/attachments/${id}`);
    const existingFiles = res?.data?.result?.data?.map((item, idx) => {
      return {
        data: {
          id: item.relationships?.file?.data?.id ?? null,
        },
        attrs: {
          fileName: item.attributes?.description ?? `File ${idx + 1}`,
        },
        uploadId: item.id, // fallback unique ID
        name: item.attributes?.description, // optional for fallback
      }
    })

    setExistingFiles(existingFiles)
  }

  const onFileUpload = (e) => {
    const files = Array.from(e.target.files)
    setErrorMsg('') // Clear previous errors before new upload
    handleFiles(files)

    // Reset input so same files can trigger onChange again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const onDrop = (e) => {
    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    setErrorMsg('')
    handleFiles(files)
  }

  const onDragEnter = () => wrapperRef.current.classList.add('border-[#7B5FA6]')
  const onDragLeave = () =>
    wrapperRef.current.classList.remove('border-[#7B5FA6]')

  const handleFiles = async (files) => {
    for (const file of files) {
      if (!acceptedTypes.includes(file.type)) {
        setErrorMsg('Unsupported file type')
        toast.error('Unsupported file type')
        continue
      }

      if (file.size > maxSize) {
        setErrorMsg('File size exceeds 10MB')
        toast.error('File size exceeds 10MB')
        continue
      }

      const existing = localStorage.getItem('uploadedFiles')
      if (existing) {
        try {
          const parsed = JSON.parse(existing)
          const duplicate = parsed.attachments?.some((attachment) => {
            const attrs = attachment?.data?.attributes
            return attrs?.fileName?.trim() === file.name?.trim()
          })
          if (duplicate) {
            toast.error(`"${file.name}" has already been uploaded`)
            continue
          }
        } catch (e) {
          console.warn('Could not parse uploadedFiles from localStorage:', e)
        }
      }

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('user_id', auth?.user?.id)
        formData.append('booking_id', id)

        const res = await axios.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            attachToBooking: isBooked
          }
        })

        if (res?.data?.error) {
          toast.error(res.data.message || 'Failed to upload file')
          continue
        }

        const newAttachment = res?.data?.result?.attachment
        const uploadId = newAttachment?.data?.id || null

        if (!uploadId) {
          toast.error('No file ID returned from upload')
          continue
        }

        let localFiles
        if (existing) {
          try {
            localFiles = JSON.parse(existing)
            localFiles.attachments.push(newAttachment)
          } catch (e) {
            console.error('Error parsing localStorage, resetting...')
            localFiles = {
              attachments: [newAttachment],
              id: id || null,
            }
          }
        } else {
          localFiles = {
            attachments: [newAttachment],
            id: id || null,
          }
        }

        localStorage.setItem('uploadedFiles', JSON.stringify(localFiles))

        setFileList((prev) => [
          ...prev,
          {
            name: file.name,
            size: file.size,
            type: file.type,
            uploadId,
          },
        ])

        setErrorMsg('')
        toast.success(`Uploaded ${file.name} successfully`)
      } catch (error) {
        console.error(`Upload failed for ${file.name}`, error)
        toast.error(`Failed to upload ${file.name}`)
      }
    }
  }

  const fileRemove = async (id) => {
    try {
      if (!id) {
        toast.error('No file ID provided for deletion')
        return
      }

      await axios.delete(`${deleteUrl}/${id}?userId=${auth.user.id}`)

      const existing = localStorage.getItem('uploadedFiles')
      if (existing) {
        const parsed = JSON.parse(existing)
        const updatedAttachments = parsed.attachments.filter(
          (attachment) => attachment?.data?.id !== id
        )

        if (updatedAttachments.length !== parsed.attachments.length) {
          const updated = {
            ...parsed,
            attachments: updatedAttachments,
          }
          localStorage.setItem('uploadedFiles', JSON.stringify(updated))
          setExistingFiles(updatedAttachments)
        }
      }

      setFileList((prev) => prev.filter((file) => file.uploadId !== id))
      toast.success(`Successfully deleted`)
    } catch (error) {
      toast.error(`Failed to delete file, please try again`)
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap">
        <div className="flex-1 min-w-[250px]">
          <h2 className="text-lg font-semibold">
            Supporting documents{' '}
            <span className="text-sm font-normal text-gray-500">
              (optional)
            </span>
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Add supporting images and documents that are relevant to this
            booking.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Photographs of an intimate nature <strong>must not</strong> be
            uploaded. Please do not send any images of areas of the body which
            would normally be covered by a bikini or swimming trunks.
          </p>
        </div>

        <section className="w-full bg-white rounded-xl shadow-md py-4 px-4 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg">Upload Documents</h2>
          </div>

          <label
            ref={wrapperRef}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="block text-center border-2 rounded-lg p-10 transition cursor-pointer hover:border-[#7B5FA6] border-dashed border-gray-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-12 w-12 text-gray-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 15a4 4 0 004 4h10a4 4 0 004-4v-1a3 3 0 00-3-3h-1.26a1.16 1.16 0 01-2.22 0H9.48a1.16 1.16 0 01-2.22 0H6a3 3 0 00-3 3v1z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7l-4-4m0 0L8 7m4-4v12"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600">
              Drag & drop your files here or click to browse
            </p>
            <input
              ref={fileInputRef} // attach ref here
              type="file"
              multiple
              accept=".jpeg,.jpg,.png,.tiff,.pdf,.doc,.docx,.xls,.xlsx,.csv"
              className="hidden"
              onChange={onFileUpload}
            />
          </label>

          {errorMsg && <p className="text-rose-500 text-sm mt-2">{errorMsg}</p>}
        </section>
        <div className="mt-4 w-full space-y-2">
          {(existingFiles?.length > 0 ? existingFiles : fileList).map((file, idx) => {
            const attrs = file?.data?.attributes || file; // Support both formats

              return (
                <div
                  key={file.data?.id || file.uploadId || idx}
                  className="flex items-center justify-between w-full bg-white text-black border border-gray-300 rounded-md px-4 py-2"
                >
                  <p className="text-sm truncate">
                    {attrs.fileName || file.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => fileRemove(file.data?.id || file.uploadId)}
                    className="text-rose-500 hover:text-red-700"
                    aria-label={`Remove file ${attrs.fileName || file.name}`}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </button>
                </div>
              )
            }
          )}
        </div>
      </div>
    </div>
  )
}

export default SupportingDocs
